import { PwaLifecycle } from '@/uptk/core/components/common/PwaControls';
import {LinkedScreenSession} from '@/uptk/core/api/LinkedScreenContext';
import * as React from 'react';
import axios from 'axios';
import { installDocCacheInvalidation } from '@/uptk/core/api/docCacheScope';
import { useLocation, useNavigate } from 'react-router';
import { Provider as ReduxProvider, useSelector } from 'react-redux';
import { setNavigateFunction } from '@/history';
import { ClerkProvider } from '@clerk/clerk-react';
import { IntlProvider } from 'react-intl';
import { LocaleContext, LocaleContextProvider } from '@/LocaleContext';
import { applyDocumentDirection } from '@/i18n/direction';
import {
  isPseudoLocaleActive,
  getDevDirOverride,
  buildPseudoMessages,
} from '@/i18n/devLocale';
import DevI18nToggle from '@/i18n/DevI18nToggle';
import { isEmbeddedView } from '@/uptk/core/utils/embed_mode';
import ClerkInitializer from '@/uptk/core/components/auth/ClerkInitializer';
import AuthContextInitializer from '@/uptk/core/components/auth/auth_context_initializer';
import { AuthContextProvider } from '@/uptk/core/context/auth-context';
import { ThemeConfigurationProvider } from '@/uptk/core/context/theme_configuration_context';
import { MsalSession } from '@/uptk/core/api/auth';
import configureStore from '@/redux/configureStore';
import { loadModuleEntitlements } from '@/redux/actions/module_entitlements_actions';
import envTheme from '@/styles/envTheme';
import messages_fr from '@/translations/fr.json';
import messages_en from '@/translations/en.json';
import messages_ar from '@/translations/ar.json';
import messages_de from '@/translations/de.json';
import messages_it from '@/translations/it.json';
import { useTranslationCatalog } from '@/uptk/core/hooks/use_translation_catalog';
import { DocLocaleProvider } from '@/uptk/core/context/doc-locale-context';
import { BrandingProvider } from '@/uptk/core/context/branding-context';
import {
  getAuthContextState,
  subscribeAuthContextState,
} from '@/uptk/core/api/auth_context_store';
import { getPreferredAccessToken } from '@/uptk/core/api/clerkTokenProvider';
import { buildSaasHeaders, resolveSaasContext, syncSaasContext } from '@/uptk/core/api/saasContext';
import { installModuleInactiveInterceptor } from '@/uptk/core/api/moduleInactive';
import { apiAzureUrl, apiLocalHttp } from '@/uptk/core/api/contants';
import { loadModuleRegistry } from '@/uptk/core/modules/registry';
import { subscribeTenantOverrideState } from '../uptk/core/api/tenant_override_store';
import { QueryProvider } from '@/uptk/core/queries/QueryProvider';
import { ToastProvider } from '@/design-system/ds-0/components/Toast';
import CopilotDrawer from '@/uptk/core/components/copilot/CopilotDrawer';
import UiVoiceHost from '@/uptk/core/components/voice/ui/UiVoiceHost';
import FormDrawerHost from '@/uptk/core/components/common/FormDrawerHost';
import JobCompletionNotifier from '@/uptk/core/components/common/JobCompletionNotifier';
import { PrintHeader } from '@/uptk/core/print/PrintHeader';
import { PrintModeBridge } from '@/uptk/core/print/PrintModeBridge';
 
// Clerk configuration
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string | undefined;
if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing Clerk Publishable Key: set VITE_CLERK_PUBLISHABLE_KEY in your environment');
}

// Initialize Redux store
const store = configureStore();

// Initialize MSAL session
const msalSession = new MsalSession();

const PROFILE_NAME = import.meta.env.VITE_PROFILES_ACTIVE;

const messages = {
  fr: messages_fr,
  en: messages_en,
};

const resolveDefaultLocale = () => {
  const language = typeof navigator !== 'undefined' && navigator.language
    ? navigator.language.split(/[-_]/)[0]
    : 'en';
  return language === 'fr' ? 'fr' : 'en';
};

type RootState = {
  locale?: string;
};

// Target product locales. `en` (and `fr` during migration) ship a bundled JSON
// baseline; `de`, `it`, `ar` are served from the DB catalog overlay. Accepting a
// locale here lets it reach the catalog fetch instead of collapsing to the default.
// Exported so app/uptk/core/i18n/localeRegistry.ts can derive the platform locale
// list from this single source instead of duplicating the array (UPT-1711).
export const SUPPORTED_LOCALES: ReadonlySet<string> = new Set(['en', 'fr', 'de', 'it', 'ar']);
// Additional RTL-capable locales with no catalog yet — direction is applied but
// messages fall back to English.
const DIRECTION_ONLY_LOCALES: ReadonlySet<string> = new Set(['he', 'fa', 'ur']);

function AppIntlProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const localeFromRedux = useSelector((state: RootState) => state.locale);
  const localeContextValue = React.useContext(LocaleContext) as [string, React.Dispatch<React.SetStateAction<string>>] | undefined;
  const localeFromContext = localeContextValue?.[0];
  // Accept catalog locales AND direction-only locales (e.g. 'ar' for layout sign-off).
  // Fall back to en when nothing recognised.
  const rawLocale = localeFromRedux ?? localeFromContext ?? resolveDefaultLocale();
  const locale = SUPPORTED_LOCALES.has(rawLocale) || DIRECTION_ONLY_LOCALES.has(rawLocale)
    ? rawLocale
    : resolveDefaultLocale();
  // Bundled baseline locale: en is always the base; fr keeps its bundled catalog during
  // migration. de/it are served purely from the DB catalog overlay (the seed has DE/IT
  // rows). ar has NO DB rows yet, so its bundled catalog is overlaid below — without
  // this, the main app renders pure English under ar even though ar.json is translated.
  const baselineLocale: 'en' | 'fr' = locale === 'fr' ? 'fr' : 'en';

  // DB-backed catalog overlay (tenant/workspace-scoped, incl. per-tenant overrides and
  // non-bundled locales). Overlaid on top of the bundled baseline below.
  const catalog = useTranslationCatalog(locale);

  // Dev-only Quality Gate affordances (UPT-172/UPT-37 §3): pseudo-locale + RTL
  // preview. No-ops in production (devLocale guards on import.meta.env.DEV).
  const pseudoActive = isPseudoLocaleActive();
  const devDir = getDevDirOverride();

  // Keep the document `dir`/`lang` in sync with the active locale so the catalog's
  // per-locale direction is honored (RTL-ready). See app/i18n/direction.ts.
  // A dev RTL-preview override (`?dir=rtl`) wins so layouts can be checked without
  // an RTL catalog.
  React.useEffect(() => {
    applyDocumentDirection(locale);
    if (devDir && typeof document !== 'undefined') {
      document.documentElement.setAttribute('dir', devDir);
    }
  }, [locale, devDir]);

  // When the pseudo-locale is on, swap in the bracketed/padded catalog built from the
  // default-locale messages; hard-coded strings then stand out as un-bracketed text.
  // Otherwise: bundled baseline (en, plus fr during migration) with the DB catalog
  // overlaid on top so tenant overrides and non-bundled locales (de/it/ar) win.
  const baselineMessages = (messages[baselineLocale] ?? messages.en) as Record<string, string>;
  // Bundled ar catalog sits between the en baseline and the DB overlay, so DB rows
  // (tenant overrides) still win once the seed grows Arabic.
  const bundledOverlay: Record<string, string> =
    locale === 'ar' ? (messages_ar as Record<string, string>)
      : locale === 'de' || locale === 'it' ? Object.fromEntries(Object.entries((locale === 'de' ? messages_de : messages_it) as Record<string, string>).filter(([key]) => (key.startsWith('doc.testplans.') || key.startsWith('pwa.'))))
      : {};
  const activeMessages = pseudoActive
    ? buildPseudoMessages(messages.en as Record<string, string>)
    : { ...baselineMessages, ...bundledOverlay, ...catalog.messages };

  const handleIntlError = React.useCallback((error: unknown) => {
    const intlError = error as { code?: string; descriptor?: { id?: string }; message?: string };
    const errorCode = intlError?.code ?? '';
    const messageId = intlError?.descriptor?.id ?? '';
    const messageText = intlError?.message ?? '';
    const isMissingTranslation = errorCode === 'MISSING_TRANSLATION' || messageText.includes('MISSING_TRANSLATION');
    const isFormHint = typeof messageId === 'string'
      && messageId.startsWith('formFields.')
      && messageId.endsWith('.hint');

    // Prevent noisy repeated warnings for optional legacy hint keys while keeping
    // all other i18n errors visible.
    if (isMissingTranslation && isFormHint) {
      return;
    }

    if (isMissingTranslation) {
      // Signal missing keys in dev/test builds so engineers can catch gaps without
      // crashing — they are silently ignored in production (fallback-to-id is intentional).
      if (import.meta.env.DEV && import.meta.env.MODE !== 'test') {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] Missing translation key: "${messageId}"`);
      }
      return;
    }

    if (typeof console !== 'undefined' && typeof console.error === 'function') {
      console.error(error);
    }
  }, []);

  return (
    <IntlProvider
      locale={locale}
      // eslint-disable-next-line i18next/no-literal-string -- i18n-exempt: locale code, not user copy
      defaultLocale="en"
      messages={activeMessages}
      onError={handleIntlError as any}
    >
      {children}
      {/* UPT-1454 — hidden in master-detail drawer iframes (?embed=1) */}
      {!isEmbeddedView() && <DevI18nToggle />}
    </IntlProvider>
  );
}

const resolveRequestUrl = (url?: string, baseURL?: string) => {
  if (typeof window === 'undefined' || !url) {
    return null;
  }

  try {
    return new URL(url, baseURL ?? window.location.origin);
  } catch {
    return null;
  }
};

const resolveBackendOrigins = () => {
  return [apiAzureUrl(), apiLocalHttp]
    .filter((candidate) => typeof candidate === 'string' && candidate.length > 0)
    .map((candidate) => {
      try {
        return new URL(candidate).origin;
      } catch {
        return '';
      }
    })
    .filter(Boolean);
};

const shouldAttachAuthenticatedHeaders = (url: URL) => {
  if (typeof window === 'undefined') {
    return false;
  }

  if (url.origin === window.location.origin) {
    return true;
  }

  return resolveBackendOrigins().includes(url.origin);
};

// Install the auth interceptor at module load time (before any React renders)
// so that every axios request gets Bearer + SaaS headers from the very first call.
// Previously this was in a useEffect, but React runs child effects before parent
// effects, causing ThemeConfigurationProvider's initial fetches to fire without
// the interceptor.
//
// SSR: only install on the browser. On the server, route loaders handle their
// own auth headers.
// The auth/context endpoint resolves the tenant from the JWT — do NOT pre-supply
// tenant headers here.  Sending a stale or config-fallback tenant causes 403 on
// fresh sessions before any real tenant has been stored.
const AUTH_CONTEXT_PATH = '/api/v1/auth/context';

// La langue choisie dans l'application (localStorage « locale », posée par le
// sélecteur de langue) doit gouverner les libellés que le serveur résout
// lui-même (OnboardingLabels, CatalogMessageResolver lisent Accept-Language).
// Sans cet en-tête, le navigateur envoyait sa propre langue : un utilisateur
// passé en français sur un navigateur anglais recevait des parcours de mise en
// route en anglais (relecture du 21.09, UPT-1972).
const resolveAcceptLanguage = (): string | null => {
  try {
    const stored = (window.localStorage.getItem('locale') ?? '').split(/[-_]/)[0].toLowerCase();
    return /^[a-z]{2}$/.test(stored) ? stored : null;
  } catch {
    return null;
  }
};

if (typeof window !== 'undefined') {
  axios.interceptors.request.use(
  async (requestConfig) => {
    const resolvedUrl = resolveRequestUrl(requestConfig.url, requestConfig.baseURL);
    if (!resolvedUrl || !shouldAttachAuthenticatedHeaders(resolvedUrl)) {
      return requestConfig;
    }

    const token = await getPreferredAccessToken();

    if (resolvedUrl.pathname === AUTH_CONTEXT_PATH) {
      requestConfig.headers = {
        ...(requestConfig.headers ?? {}),
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      } as any;
      return requestConfig;
    }

    const existingHeaders = (requestConfig.headers ?? {}) as Record<string, string>;
    const saasContext = resolveSaasContext({
      tenantId: existingHeaders.tenantid ?? existingHeaders.tenantId,
      organizationId: existingHeaders.organizationid ?? existingHeaders.organizationId,
      workspaceId: existingHeaders.workspaceid ?? existingHeaders.workspaceId,
    });
    const acceptLanguage = existingHeaders['Accept-Language'] ? null : resolveAcceptLanguage();

    requestConfig.headers = {
      ...existingHeaders,
      ...buildSaasHeaders(saasContext),
      ...(acceptLanguage ? { 'Accept-Language': acceptLanguage } : {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    } as any;

    return requestConfig;
  },
  (error: unknown) => Promise.reject(error instanceof Error ? error : new Error('Request interception failed.'))
);
// UPT-2069 (L2) : intercepteur de réponse commun — un 403 `module.inactive` est publié (état « module non activé »
// des pages de module, ModuleInactiveBoundary) ; le rejet reste transmis tel quel à l'appelant.
installModuleInactiveInterceptor(axios);
} // end browser-only interceptor guard

export function ClientProviders({ children }: Readonly<{ children: React.ReactNode }>) {
  React.useEffect(installDocCacheInvalidation, []);
  const location = useLocation();
  const navigate = useNavigate();

  // Initialize the legacy history shim with React Router's navigate
  React.useEffect(() => {
    setNavigateFunction(navigate);
  }, [navigate]);

  React.useEffect(() => {
    const dispatch = store.dispatch as (action: unknown) => unknown;
    const syncTenantScopedState = () => {
      const authContext = getAuthContextState();
      if (!authContext) {
        return;
      }

      const context = syncSaasContext({ authContext });
      const tenantId = context.tenantId.trim();
      if (!tenantId) {
        return;
      }

      void loadModuleRegistry(tenantId);
      dispatch(loadModuleEntitlements({ tenantId }));
    };

    const unsubscribeAuthContext = subscribeAuthContextState(() => {
      void syncTenantScopedState();
    });
    const unsubscribeTenantOverride = subscribeTenantOverrideState(() => {
      void syncTenantScopedState();
    });
    void syncTenantScopedState();

    return () => {
      unsubscribeAuthContext();
      unsubscribeTenantOverride();
    };
  }, []);

  React.useEffect(() => {
    async function initialize() {
      try {
        await msalSession.configure();
      } catch {
        // Continue anyway with current theme
      }
    }

    void initialize();
  }, []);

  return (
    <QueryProvider>
    <ReduxProvider store={store}>
      <LocaleContextProvider>
        <AppIntlProvider>
          <AuthContextProvider auth={msalSession as unknown as Record<string, unknown>}>
            <ClerkProvider allowedRedirectOrigins={typeof window === 'undefined' ? [] : [window.location.origin]} publishableKey={CLERK_PUBLISHABLE_KEY} afterSignOutUrl="/">
              <ClerkInitializer />
              <AuthContextInitializer
                onContextReady={() => {
                  void msalSession.configure();
                  syncSaasContext({ authContext: getAuthContextState() });
                }}
              >
                <ThemeConfigurationProvider
                  pathname={location.pathname}
                  profileName={PROFILE_NAME}
                  envTheme={envTheme}
                  session={msalSession}
                >
                  <DocLocaleProvider>
                    {/* Session branding cascade (TENANT > MAKER > PLATFORM) —
                        owns favicon + document.title side effects. */}
                    <BrandingProvider>
                    {/* Design-system toast context — consumed by CommentThread (UPT-947)
                        and any ds-0 component calling useToast(). */}
                    <ToastProvider>
                      {/* UPT-2049 — mode impression des fiches : écouteurs
                          beforeprint / afterprint (Ctrl+P) et en-tête d'impression
                          (invisible à l'écran, cf. design-system/print.css). */}
                      <PwaLifecycle />
                      <PrintModeBridge />
                      <PrintHeader />
                      <LinkedScreenSession>{children}</LinkedScreenSession>
                      {/* Geode Copilot (UPT-1437 Phase 0) — renders only when signed in
                          on a portal route; see CopilotDrawer for the SSE protocol.
                          UPT-1454: hidden in master-detail drawer iframes (?embed=1).
                          UPT-2049: jamais sur le papier (enveloppe masquée à l'impression). */}
                      {!isEmbeddedView() && (
                        <div data-print-hide="true" className="contents">
                          <CopilotDrawer />
                          {/* UPT-2124 J4 — Géode pilote l'interface : outils vocaux ui_* + fantôme (voix visible, connecté, portails). */}
                          <UiVoiceHost />
                        </div>
                      )}
                      {/* UAT 10.10.2026 : tiroir de fiche (prise de rendez-vous et fiches Quenotte), cf. formDrawer.ts. */}
                      {!isEmbeddedView() && <FormDrawerHost />}
                      {/* Batch-job completion feedback (STOMP /topic/jobs) — slides in
                          from any page when a session-launched job finishes. */}
                      {!isEmbeddedView() && (
                        <div data-print-hide="true" className="contents">
                          <JobCompletionNotifier />
                        </div>
                      )}
                    </ToastProvider>
                    </BrandingProvider>
                  </DocLocaleProvider>
                </ThemeConfigurationProvider>
              </AuthContextInitializer>
            </ClerkProvider>
          </AuthContextProvider>
        </AppIntlProvider>
      </LocaleContextProvider>
    </ReduxProvider>
    </QueryProvider>
  );
}
